import {
    Box,
    Button,
    IconButton,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    AlertTriangle,
    ArrowLeft,
    ChevronDown,
    Eye,
    Info,
    Pencil,
    Star,
    Trash2,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { httpClient } from '../../../common/httpclient';
import DialogForm from '../../../component/dialog/DialogForm';
import PageScaffold from '../../../component/pageScaffold/PageScaffold';
import { usePermissions } from '../../../hooks/usePermissions';
import useIsMobileShell from '../../../hooks/useIsMobileShell';
import { useAppBarAction, useAppBarTitle } from '../../../component/layout/AppBarActions';
import { MobileChecklistDetail } from '../../../component/checklist/MobileChecklist';
import { KitButton } from '../../../component/common/MobileKit';
import { KitEmpty } from '../../../component/common/MobileKitExtras';
import { protoIcon } from '../../../component/common/ProtoIcon';

const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');
const AlertIcon = protoIcon('alert');

// ============================================================================
// VISUALIZAÇÃO DE TEMPLATE (item 132, "Visualização de template") — página
// standalone somente-leitura da rota `/checklist/:id/view`, mesmo shell de
// `StandaloneChecklistForm`/`StandaloneGroupForm` (`Form.js`/`GroupForm.js`):
// `view-hero` (voltar + nome + pill "Padrão" + descrição, SEM anel de
// progresso por ser somente leitura) → card "Dados do template" (`ro-grid`,
// mesmo padrão de `pages/customers/View.js`) → card "Grupos de inspeção do
// template" com a estrutura COMPLETA (todos os grupos + todos os itens +
// chips de resposta OK/NOK/Manutenção, recolhível por grupo) → coluna sticky
// de resumo. Ref: `prototipo-visualizacao-checklist.html`.
//
// Substitui o antigo `ChecklistView` (drawer com `LegacyEmbeddedChecklistForm`
// dentro + `ToggleButtonGroup` de preenchimento) — aquele modo de
// "preenchimento" nunca fazia sentido aqui (não salvava em lugar nenhum);
// preenchimento de verdade é `Filler.js`/`ChecklistFillerModal.js`. "Editar
// cadastro" agora navega de verdade para o editor de template de página
// cheia (`/checklist/:id/edt`, item 129) em vez de abrir um `Drawer` com o
// formulário legado embutido.
// ============================================================================

// Classifica um `Feedback` (group_feedback) em ok/nok/manut pela mesma
// heurística de nome já usada pelo antigo `ChecklistView`/`GroupForm.js`
// (`ANSWER_ALIASES`: grupos antigos usavam "Atenção" em vez de "NOK").
function classifyFeedback(name) {
    const upper = String(name || '').toUpperCase();
    if (upper === 'OK') return 'ok';
    if (upper === 'NOK' || upper.includes('ATEN')) return 'nok';
    if (upper.includes('MANUTEN')) return 'manut';
    return 'other';
}

function answerTone(theme, kind) {
    switch (kind) {
        case 'ok':
            return { bg: theme.palette.success.bg, fg: theme.palette.success.main };
        case 'nok':
            return { bg: theme.palette.error.bg, fg: theme.palette.error.main };
        case 'manut':
            return { bg: theme.palette.warning.bg, fg: theme.palette.warning.main };
        default:
            return { bg: theme.palette.background.subtle, fg: theme.palette.text.secondary };
    }
}

const DEFAULT_FEEDBACK_OPTIONS = [
    { feedback_id: 'ok', feedback_name: 'OK', feedback_order: 1 },
    { feedback_id: 'nok', feedback_name: 'NOK', feedback_order: 2 },
    { feedback_id: 'manutencao', feedback_name: 'Manutenção', feedback_order: 3 },
];

// `check-answers` somente-leitura — chip preenchido (sem interação), mesma
// paleta de tons de `StatusChip domain="checklist"`/`GroupForm.js`.
function AnswerChipReadOnly({ name }) {
    const theme = useTheme();
    const kind = classifyFeedback(name);
    const { bg, fg } = answerTone(theme, kind);
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '5px 11px',
                borderRadius: '20px',
                fontSize: 10.5,
                fontWeight: 700,
                lineHeight: 1,
                backgroundColor: bg,
                color: fg,
                whiteSpace: 'nowrap',
            }}
        >
            {name}
        </Box>
    );
}

// Pill "Padrão" (verde + estrela) — mesma convenção de `DefaultPill`
// (`List.js`/`ViewDialog.js`), aqui compacta o bastante para conviver com o
// título do hero e a linha do resumo sticky.
function DefaultPillSmall() {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '11px',
                fontWeight: 700,
                lineHeight: 1,
                padding: '4px 10px',
                borderRadius: '20px',
                bgcolor: 'success.bg',
                color: 'success.main',
                verticalAlign: 'middle',
            }}
        >
            <Star size={11} strokeWidth={0} fill="currentColor" />
            Padrão
        </Box>
    );
}

// `.ro-field` — label uppercase micro + valor (DESIGN-SYSTEM.md §5.5,
// mesmo padrão de `pages/customers/View.js` `RoField`).
function RoField({ label, value, muted, full }) {
    return (
        <Box sx={{ gridColumn: full ? '1 / -1' : 'auto', minWidth: 0 }}>
            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em', mb: '4px' }}>
                {label}
            </Typography>
            {typeof value === 'string' || value == null ? (
                <Typography sx={{ fontSize: '13.5px', fontWeight: muted ? 500 : 600, color: muted ? 'text.disabled' : 'text.primary', lineHeight: 1.4 }}>
                    {value || '—'}
                </Typography>
            ) : (
                <Box sx={{ mt: '2px' }}>{value}</Box>
            )}
        </Box>
    );
}

function SummaryRow({ label, value }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', fontSize: '12.5px', py: '7px', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
            <span>{label}</span>
            <Box component="b" sx={{ color: 'text.primary', textAlign: 'right' }}>{value}</Box>
        </Box>
    );
}

// `check-group`/`check-item`/`check-answers` somente-leitura, com toggle de
// recolher/expandir por grupo e botão "Editar grupo" no cabeçalho (navega
// para o editor de grupo, item 130).
function ReadOnlyCheckGroup({ group, collapsed, onToggleCollapse, onEditGroup, canEditGroup }) {
    const theme = useTheme();
    const items = useMemo(() => (
        Array.isArray(group.group_inspection_items)
            ? group.group_inspection_items
                .filter((it) => it.active !== false)
                .sort((a, b) => (a.insp_item_order || 0) - (b.insp_item_order || 0))
            : []
    ), [group.group_inspection_items]);

    const feedbackOptions = useMemo(() => {
        const active = Array.isArray(group.group_feedback)
            ? group.group_feedback.filter((f) => f.active !== false).sort((a, b) => (a.feedback_order || 0) - (b.feedback_order || 0))
            : [];
        return active.length > 0 ? active : DEFAULT_FEEDBACK_OPTIONS;
    }, [group.group_feedback]);

    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflow: 'hidden', mb: '14px', '&:last-child': { mb: 0 } }}>
            <Box
                onClick={onToggleCollapse}
                sx={{
                    p: '11px 14px',
                    background: theme.palette.gradient.brand,
                    color: '#fff',
                    fontSize: 12.5,
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <ChevronDown
                    size={15}
                    strokeWidth={2.4}
                    style={{ flexShrink: 0, opacity: 0.9, transition: 'transform .2s ease', transform: collapsed ? 'rotate(-90deg)' : 'none' }}
                />
                <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {group.group_name}
                </Box>
                <Box component="span" sx={{ fontSize: '10.5px', fontWeight: 700, bgcolor: 'rgba(255,255,255,.2)', px: '8px', py: '3px', borderRadius: '20px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {items.length} ite{items.length === 1 ? 'm' : 'ns'}
                </Box>
                {canEditGroup && (
                    <Box
                        component="button"
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onEditGroup(); }}
                        title="Editar itens e respostas deste grupo"
                        sx={{
                            fontFamily: 'inherit',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            padding: '5px 9px',
                            borderRadius: '8px',
                            fontSize: '10.5px',
                            fontWeight: 700,
                            backgroundColor: 'rgba(255,255,255,.16)',
                            border: '1px solid rgba(255,255,255,.3)',
                            color: '#fff',
                            cursor: 'pointer',
                            flexShrink: 0,
                            '&:hover': { backgroundColor: 'rgba(255,255,255,.28)' },
                        }}
                    >
                        <Pencil size={12} strokeWidth={2.4} />
                        Editar grupo
                    </Box>
                )}
            </Box>

            {!collapsed && (
                <>
                    <Box sx={{ p: '9px 14px', fontSize: 11.5, color: 'text.secondary', backgroundColor: 'background.subtle', borderBottom: '1px solid', borderColor: 'divider' }}>
                        {group.group_description || 'Sem descrição.'}
                    </Box>
                    <Box>
                        {items.length === 0 ? (
                            <Typography sx={{ fontSize: 12, color: 'text.disabled', p: '16px 14px', textAlign: 'center' }}>
                                Nenhum item de inspeção cadastrado neste grupo.
                            </Typography>
                        ) : items.map((item, idx) => (
                            <Box
                                key={item.insp_item_id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    p: '10px 14px',
                                    flexWrap: 'wrap',
                                    borderBottom: idx < items.length - 1 ? '1px solid' : 'none',
                                    borderColor: 'divider',
                                }}
                            >
                                <Box sx={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: '6px',
                                    backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider', fontSize: 10, fontWeight: 800, color: 'text.disabled', flexShrink: 0,
                                }}>
                                    {idx + 1}
                                </Box>
                                <Typography sx={{ flex: 1, minWidth: 140, fontSize: 12.5, fontWeight: 600, color: 'text.primary' }}>
                                    {item.insp_item_name}
                                </Typography>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ flexShrink: 0, rowGap: '6px' }}>
                                    {feedbackOptions.map((fb) => (
                                        <AnswerChipReadOnly key={fb.feedback_id} name={fb.feedback_name} />
                                    ))}
                                </Stack>
                            </Box>
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
}

export default function ChecklistView() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { id } = useParams();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('success');

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [checklist, setChecklist] = useState(null);
    const [groups, setGroups] = useState([]);
    const [collapsedGroups, setCollapsedGroups] = useState({});

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const showError = (msg) => {
        setSeverity('error');
        setMessage(typeof msg === 'string' ? msg : 'Erro ao processar a solicitação');
        setOpenSnackbar(true);
    };

    const fetchChecklist = async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const [detailRes, viewRes] = await Promise.all([
                httpClient.get(`/checklist/${id}`),
                httpClient.get(`/checklist/${id}/view`),
            ]);

            const detail = detailRes.data;
            if (!detail || !detail.id) {
                setLoadError(true);
                return;
            }
            setChecklist(detail);

            // O array de `/checklist/{id}/view` não é ordenado pelo backend
            // (`GetGroupChecklist`, `fv-api/checklist/repository.go`, não tem
            // `ORDER BY`) — a ordem correta de anexação vem de
            // `GET /checklist/{id}` (`detail.groups`, ordenado por
            // `order_view`). Reordenamos aqui por id sem exigir mudança de
            // contrato no backend; qualquer grupo presente na view mas
            // ausente da lista de ordem (inconsistência de dados) cai no
            // final, na ordem em que a view o retornou.
            const rawGroups = Array.isArray(viewRes.data) ? viewRes.data : [];
            const orderIndex = new Map((detail.groups || []).map((g, idx) => [g.id, idx]));
            const orderedGroups = [...rawGroups].sort((a, b) => {
                const ai = orderIndex.has(a.group_id) ? orderIndex.get(a.group_id) : Number.MAX_SAFE_INTEGER;
                const bi = orderIndex.has(b.group_id) ? orderIndex.get(b.group_id) : Number.MAX_SAFE_INTEGER;
                return ai - bi;
            });
            setGroups(orderedGroups);
        } catch (error) {
            console.error('Erro ao carregar template de checklist:', error);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchChecklist();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const toggleGroup = (groupId) => {
        setCollapsedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
    };

    const groupsCount = groups.length;
    const itemsCount = groups.reduce((sum, g) => {
        const items = Array.isArray(g.group_inspection_items) ? g.group_inspection_items.filter((it) => it.active !== false) : [];
        return sum + items.length;
    }, 0);

    const handleEditTemplate = () => navigate(`/checklist/${id}/edt`);
    const handleEditGroup = (groupId) => navigate(`/checklist/${id}/group/edt?groupId=${groupId}`);

    // Mesma regra de bloqueio do template padrão já usada em `List.js`: o
    // backend (`DeleteChecklist`) faz apenas soft delete e não impede a
    // exclusão do padrão — a regra é 100% de front.
    const handleClickDelete = () => {
        if (!checklist) return;
        if (checklist.is_default) {
            showError('Não é possível excluir o template padrão. Defina outro como padrão antes de excluir este.');
            return;
        }
        setOpenDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        setDeleting(true);
        try {
            const response = await httpClient.delete(`/checklist/${id}`);
            if (response.status === 204 || response.status === 200) {
                navigate('/vehicle_checklist');
                return;
            }
            showError(response.data || 'Erro ao excluir template');
        } catch (error) {
            console.error('Erro ao excluir checklist:', error);
            showError(error.response?.data || error.message || 'Erro ao excluir template');
        } finally {
            setDeleting(false);
            setOpenDeleteDialog(false);
        }
    };

    // Shell mobile (`checklists/detalhe.html`): título = nome do template; ações editar (e excluir) na barra.
    useAppBarTitle(isMobileShell ? (checklist?.name || 'Checklist') : '');
    useAppBarAction({
        enabled: isMobileShell && !loading && !loadError && (hasPermission('checklist.update') || hasPermission('checklist.delete')),
        actions: [
            ...(hasPermission('checklist.update') ? [{ label: 'Editar', icon: EditIcon, onClick: handleEditTemplate }] : []),
            ...(hasPermission('checklist.delete') ? [{ label: 'Excluir', icon: TrashIcon, onClick: handleClickDelete }] : []),
        ],
    });

    const cardSx = {
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: `${theme.customRadius.card}px`,
        boxShadow: theme.customShadows.sm,
        backgroundColor: 'background.paper',
        p: '20px 22px',
        mb: '20px',
    };

    const roGridSx = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px' };

    if (isMobileShell) {
        return (
            <PageScaffold
                titlePageTarget="Visualizar Template"
                links={[{ title: 'Templates de Checklist', link: '/vehicle_checklist' }]}
                message={message}
                severity={severity}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
            >
                {loadError ? (
                    <KitEmpty icon={AlertIcon} title="Template não encontrado">
                        Verifique se o link está correto ou volte para a lista de checklists.
                        <Box sx={{ mt: '16px' }}>
                            <KitButton variant="primary" onClick={() => navigate('/vehicle_checklist')}>Voltar para Checklists</KitButton>
                        </Box>
                    </KitEmpty>
                ) : loading ? (
                    <Stack spacing={1.75}>
                        <Skeleton variant="rounded" height={72} sx={{ borderRadius: '16px' }} />
                        <Skeleton variant="rounded" height={200} sx={{ borderRadius: '16px' }} />
                    </Stack>
                ) : (
                    <MobileChecklistDetail
                        checklist={checklist}
                        groups={groups}
                        itemsCount={itemsCount}
                        canEditGroup={hasPermission('checklist.update')}
                        onEditGroup={handleEditGroup}
                    />
                )}
                <DialogForm
                    iconType="warning"
                    open={openDeleteDialog}
                    title="Atenção"
                    confirmButtonText="Sim, Excluir"
                    content={`Tem certeza que deseja excluir o template "${checklist?.name || ''}"?`}
                    secondaryContent="Esta ação não pode ser desfeita. O template será removido permanentemente do sistema."
                    onSubmit={handleConfirmDelete}
                    onClose={() => setOpenDeleteDialog(false)}
                    loading={deleting}
                    loadingText="Removendo..."
                />
            </PageScaffold>
        );
    }

    if (loadError) {
        return (
            <PageScaffold
                titlePageTarget="Visualizar Template"
                links={[{ title: 'Templates de Checklist', link: '/vehicle_checklist' }]}
                message={message}
                severity={severity}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
            >
                <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto' }}>
                    <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6, textAlign: 'center' }}>
                        <Box sx={{ width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'error.bg', color: 'error.main' }}>
                            <AlertTriangle size={26} strokeWidth={1.8} />
                        </Box>
                        <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>Template não encontrado</Typography>
                        <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', maxWidth: 360 }}>
                            Verifique se o link está correto ou volte para a lista de templates de checklist.
                        </Typography>
                        <Button variant="contained" startIcon={<ArrowLeft size={14} strokeWidth={2} />} onClick={() => navigate('/vehicle_checklist')} sx={{ mt: 1 }}>
                            Voltar para Templates de Checklist
                        </Button>
                    </Box>
                </Box>
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            titlePageTarget="Visualizar Template"
            links={[{ title: 'Templates de Checklist', link: '/vehicle_checklist' }]}
            message={message}
            severity={severity}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* view-hero — voltar + título + pill "Padrão" + descrição, sem anel de progresso (somente leitura). */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap', mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '14px', minWidth: 0 }}>
                        <IconButton
                            onClick={() => navigate('/vehicle_checklist')}
                            title="Voltar para Templates de Checklist"
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', flexShrink: 0, mt: '2px' }}
                        >
                            <ArrowLeft size={18} strokeWidth={2} />
                        </IconButton>
                        <Box sx={{ minWidth: 0 }}>
                            {loading ? (
                                <Skeleton variant="text" width={240} height={32} />
                            ) : (
                                <Typography component="h1" sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.75, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                                    {checklist?.name || '—'}
                                    {checklist?.is_default && <DefaultPillSmall />}
                                </Typography>
                            )}
                            {loading ? (
                                <Skeleton variant="text" width={320} height={20} />
                            ) : (
                                <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', maxWidth: 560, lineHeight: 1.5 }}>
                                    {checklist?.description || 'Sem descrição cadastrada.'}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', flexShrink: 0 }}>
                        {hasPermission('checklist.delete') && (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<Trash2 size={14} strokeWidth={2} />}
                                onClick={handleClickDelete}
                                disabled={loading}
                            >
                                Excluir
                            </Button>
                        )}
                        {hasPermission('checklist.update') && (
                            <Button
                                variant="contained"
                                startIcon={<Pencil size={14} strokeWidth={2} />}
                                onClick={handleEditTemplate}
                                disabled={loading}
                            >
                                Editar template
                            </Button>
                        )}
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 320px' }, gap: '20px' }}>
                        <Stack spacing={1.75}>
                            <Skeleton variant="rounded" height={160} sx={{ borderRadius: '14px' }} />
                            <Skeleton variant="rounded" height={280} sx={{ borderRadius: '14px' }} />
                        </Stack>
                        <Skeleton variant="rounded" height={260} sx={{ borderRadius: '18px' }} />
                    </Box>
                ) : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 320px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                        <Box>
                            {/* CARD — Dados do template. */}
                            <Box sx={cardSx}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 800, mb: '3px' }}>Dados do template</Typography>
                                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', maxWidth: 520, lineHeight: 1.5 }}>
                                        Informações cadastrais do template, somente leitura.
                                    </Typography>
                                </Box>
                                <Box sx={roGridSx}>
                                    <RoField full label="Descrição" value={checklist?.description} muted={!checklist?.description} />
                                    <RoField
                                        label="Template padrão"
                                        value={checklist?.is_default ? 'Sim, é o template padrão' : 'Não'}
                                        muted={!checklist?.is_default}
                                    />
                                    <RoField label="Grupos de inspeção" value={`${groupsCount} grupo${groupsCount === 1 ? '' : 's'}`} />
                                    <RoField label="Itens de inspeção (total)" value={`${itemsCount} ite${itemsCount === 1 ? 'm' : 'ns'}`} />
                                    {/* `vehicle_checklists`/`checklist_evaluation_types` não têm colunas de
                                        auditoria (`created_at`/`updated_at`) — mesma lacuna de schema já
                                        documentada em `GroupForm.js` (resposta padrão por item). Sem
                                        endpoint/coluna novos, este campo fica indisponível. */}
                                    <RoField label="Última atualização" value="Não disponível" muted />
                                </Box>
                            </Box>

                            {/* CARD — Grupos de inspeção do template (estrutura completa). */}
                            <Box sx={cardSx}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: 15, fontWeight: 800, mb: '3px' }}>Grupos de inspeção do template</Typography>
                                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary', maxWidth: 560, lineHeight: 1.5 }}>
                                        Estrutura completa, na ordem em que os grupos e itens aparecem ao preencher este checklist numa ordem de serviço. Clique no cabeçalho de um grupo para recolher/expandir.
                                    </Typography>
                                </Box>

                                {groups.length === 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: '36px 20px', color: 'text.disabled', textAlign: 'center', gap: '10px' }}>
                                        <Box sx={{ width: 56, height: 56, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Info size={22} strokeWidth={1.6} />
                                        </Box>
                                        <Typography sx={{ color: 'text.primary', fontSize: '13.5px', fontWeight: 700 }}>Nenhum grupo de inspeção anexado</Typography>
                                        <Typography sx={{ fontSize: '12.5px', maxWidth: 340 }}>
                                            Use &quot;Editar template&quot; acima para anexar grupos de inspeção a este template.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box>
                                        {groups.map((group) => (
                                            <ReadOnlyCheckGroup
                                                key={group.group_id}
                                                group={group}
                                                collapsed={!!collapsedGroups[group.group_id]}
                                                onToggleCollapse={() => toggleGroup(group.group_id)}
                                                onEditGroup={() => handleEditGroup(group.group_id)}
                                                canEditGroup={hasPermission('checklist.update')}
                                            />
                                        ))}
                                    </Box>
                                )}

                                {/* groups-note — explica a fronteira de escopo desta tela somente-leitura. */}
                                <Box sx={{
                                    display: 'flex', alignItems: 'flex-start', gap: '10px', mt: 2, p: '12px 14px',
                                    borderRadius: '12px', backgroundColor: 'background.subtle', border: '1px solid', borderColor: 'divider',
                                }}>
                                    <Info size={16} strokeWidth={2} color={theme.palette.text.disabled} style={{ marginTop: 1, flexShrink: 0 }} />
                                    <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
                                        <Box component="span" sx={{ display: 'block', fontWeight: 700, color: 'text.primary', fontSize: '12.5px', mb: '2px' }}>
                                            Tela somente leitura
                                        </Box>
                                        Para alterar o nome/descrição do template ou a lista/ordem de grupos anexados, use{' '}
                                        <Box
                                            component="span"
                                            role="link"
                                            tabIndex={0}
                                            onClick={handleEditTemplate}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleEditTemplate(); }}
                                            sx={{ color: 'primary.main', fontWeight: 700, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                        >
                                            Editar template
                                        </Box>
                                        . Para alterar os itens e respostas de um grupo específico, use o botão &quot;Editar grupo&quot; em cada cabeçalho acima.
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* RESUMO STICKY */}
                        <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                            <Typography sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', mb: 1.25 }}>
                                <Eye size={13} strokeWidth={2} /> Resumo
                            </Typography>
                            <Box sx={{
                                border: '1px solid', borderColor: 'divider', borderRadius: `${theme.customRadius.hero - 2}px`,
                                backgroundColor: 'background.paper', boxShadow: theme.customShadows.md, overflow: 'hidden', mb: 1.75,
                            }}>
                                <Box sx={{ p: '16px 18px' }}>
                                    <SummaryRow label="Template padrão" value={checklist?.is_default ? <DefaultPillSmall /> : 'Não'} />
                                    <SummaryRow label="Grupos anexados" value={groupsCount} />
                                    <SummaryRow label="Itens de inspeção" value={itemsCount} />
                                    <SummaryRow label="Criado em" value={<Box component="span" sx={{ color: 'text.disabled', fontWeight: 600 }}>Não disponível</Box>} />
                                    <SummaryRow label="Atualizado em" value={<Box component="span" sx={{ color: 'text.disabled', fontWeight: 600 }}>Não disponível</Box>} />
                                </Box>
                            </Box>
                            <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.5 }}>
                                As datas de criação/atualização não estão disponíveis: o schema atual (tabela vehicle_checklists) não possui colunas de auditoria para este cadastro.
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            <DialogForm
                iconType="warning"
                open={openDeleteDialog}
                title="Atenção"
                confirmButtonText="Sim, Excluir"
                content={`Tem certeza que deseja excluir o template "${checklist?.name || ''}"?`}
                secondaryContent="Esta ação não pode ser desfeita. O template será removido permanentemente do sistema."
                onSubmit={handleConfirmDelete}
                onClose={() => setOpenDeleteDialog(false)}
                loading={deleting}
                loadingText="Removendo..."
            />
        </PageScaffold>
    );
}
